import { Table, TableRow, TableSorter } from './types';

export type RowCompareFn = (a: TableRow, b: TableRow) => number;
export type GroupHeadFindFn = (row: TableRow) => boolean;

function reverse(fn: RowCompareFn): RowCompareFn {
  return (...args) => -fn(...args);
}

abstract class TableSorterBase implements TableSorter {
  public sortedColumnIndex: number | null = null;
  public ascending: boolean;
  public onSort?: () => unknown;

  protected constructor(
    protected readonly table: Table,
    protected readonly compareFns: Map<number, RowCompareFn>
  ) {
    if (!table || !compareFns) {
      throw new Error('Invalid arguments.');
    }
  }

  public sort(columnIndex: number): void {
    const compareFn = this.compareFns.get(columnIndex);
    if (!compareFn) {
      throw new Error(`Missing compare function for column ${columnIndex}`);
    }

    if (columnIndex === this.sortedColumnIndex) {
      if (this.ascending) {
        this.sortDescending(compareFn);
      } else {
        this.sortedColumnIndex = null;
        this.sortDefault();
      }
    } else {
      this.sortedColumnIndex = columnIndex;
      this.sortAscending(compareFn);
    }
  }

  public copySortingFromOtherSorter(other: TableSorter) {
    const columnIndex: number = other.sortedColumnIndex;

    if (columnIndex === null) {
      return;
    }

    const compareFn: RowCompareFn = this.compareFns.get(columnIndex);

    if (!compareFn) {
      return;
    }

    this.sortedColumnIndex = columnIndex;
    this.ascending = other.ascending;
    if (this.ascending) {
      this.sortAscending(compareFn);
    } else {
      this.sortDescending(compareFn);
    }
  }

  public sortDefault(): void {
    this.sortedColumnIndex = null;
    this.table.rows = this.getSortedRowsDefaultOrder();
    this.onSort?.();
  }

  private sortAscending(compareFn: RowCompareFn): void {
    this.ascending = true;
    this.table.rows = this.getSortedRows(compareFn);
    this.onSort?.();
  }

  private sortDescending(compareFn: RowCompareFn): void {
    this.ascending = false;
    this.table.rows = this.getSortedRows(reverse(compareFn));
    this.onSort?.();
  }

  protected abstract getSortedRowsDefaultOrder(): TableRow[];

  protected abstract getSortedRows(compareFn: RowCompareFn): TableRow[];
}

export class GroupedTableSorter extends TableSorterBase {
  private readonly defaultOrder: TableRow[][];

  constructor(
    table: Table,
    groups: TableRow[][],
    compareFns: Map<number, RowCompareFn>,
    private readonly groupHeadFindFn: GroupHeadFindFn | null | undefined
  ) {
    super(table, compareFns);

    if (!groups) {
      throw new Error('Invalid arguments.');
    }

    this.defaultOrder = groups.map(nested => Array.from(nested));
    this.sortDefault();
  }

  protected override getSortedRowsDefaultOrder(): TableRow[] {
    this.placeSeperators(this.defaultOrder);
    return this.defaultOrder.flat(1);
  }

  protected override getSortedRows(compareFn: RowCompareFn): TableRow[] {
    const groups: TableRow[][] = this.copy();

    if (groups.length === 0) {
      return [];
    }

    for (const group of groups) {
      if (group.length === 0) {
        continue;
      }

      let head: TableRow | undefined;

      if (this.groupHeadFindFn) {
        const headIndex: number = group.findIndex(this.groupHeadFindFn);
        if (headIndex >= 0) {
          head = group.splice(headIndex, 1)[0];
        }
      }

      group.sort(compareFn);

      if (head) {
        group.splice(0, 0, head);
      }
    }

    groups.sort((groupA, groupB) => compareFn(groupA[0], groupB[0]));

    this.placeSeperators(groups);

    return groups.flat(1);
  }

  private placeSeperators(groups: TableRow[][]): void {
    if (groups.length === 0) {
      return;
    }

    for (const group of groups) {
      for (const row of group) {
        row.seperate = false;
      }

      if (group.length > 0) {
        group[group.length - 1].seperate = true;
      }
    }

    const lastGroup: TableRow[] = groups[groups.length - 1];

    if (lastGroup.length > 0) {
      lastGroup[lastGroup.length - 1].seperate = false;
    }
  }

  private copy(): TableRow[][] {
    return this.defaultOrder.map(nested => Array.from(nested));
  }
}

export class FlatTableSorter extends TableSorterBase {
  private readonly defaultOrder: TableRow[];

  constructor(table: Table, compareFns: Map<number, RowCompareFn>) {
    super(table, compareFns);
    this.defaultOrder = Array.from(table.rows);
  }

  protected override getSortedRowsDefaultOrder(): TableRow[] {
    return Array.from(this.defaultOrder);
  }

  protected override getSortedRows(compareFn: RowCompareFn): TableRow[] {
    return this.table.rows.sort(compareFn);
  }
}
